import {
  isProPlan,
  isTeamOrAgencyPlan,
  isTeamPlan,
  PLAN_FREE,
} from '@/constants/userSubscriptionPlans'
import { ProjectOrigin } from '@/types'

export enum GtmTriggerName {
  // AI
  AI_DASH_CLICK = 'gtm_ai_dash_click',
  AI_PROJECT_GENERATE = 'gtm_ai_project_generate',
  // OCT-5102: AI Generate from inside a project lands as a new section.
  AI_SECTION_GENERATE = 'gtm_ai_section_generate',
  AI_PAGE_GENERATE = 'gtm_ai_page_generate',
  AI_BLOCK_GENERATE = 'gtm_ai_block_generate',
  AI_PROJECT_COLORS = 'gtm_ai_project_colors',
  AI_PROJECT_TRANSLATE = 'gtm_ai_project_translate',
  AI_PROJECT_SEO = 'gtm_ai_project_seo',
  AI_PROJECT_CONNECT_AGENTS = 'gtm_ai_project_connect_agents',
  AI_CHAT_PROMPT_SEND = 'gtm_ai_project_prompt_send',
  AI_CHAT_RESPONSE_ACTION = 'gtm_ai_project_response_action',
  AI_CHAT_RESPONSE_NO_ACTION = 'gtm_ai_project_response_question',
  AI_CHAT_RESPONSE_ERROR = 'gtm_ai_project_response_error',
  AI_CHAT_UPGRADE = 'gtm_upgrade_ai_assistant',

  // Arrows
  ARROW_ADD = 'gtm_arrow_add',
  ARROWS_HIDE = 'gtm_arrows_hide',

  // Crawler
  CRAWL_IMPORT_PAGES = 'gtm_crawl_import_pages',
  CRAWL_IMPORT_SEO = 'gtm_crawl_import_seo',
  CRAWL_IMPORT_BLOCKS = 'gtm_crawl_import_blocks',
  CRAWL_FILTER_CHECK = 'gtm_crawl_filter_check',
  CRAWL_FILTER_CONTINUE = 'gtm_crawl_filter_continue',
  CRAWL_BACKGROUND_RUN = 'gtm_crawl_background_run',
  // Crawl landed in a project that already existed, rather than creating a new one.
  // Separate from CRAWL_IMPORT_PAGES so the two funnels stay tellable apart in GA.
  CRAWL_IMPORT_INTO_PROJECT = 'gtm_crawl_import_into_project',

  // Sitemap
  CRAWL_SITEMAP_PAGES = 'gtm_crawl_sitemap_pages',
  CRAWL_SITEMAP_SEO = 'gtm_crawl_sitemap_seo',
  CRAWL_SITEMAP_MULTI = 'gtm_crawl_sitemap_multi',

  // Sitemap report
  REPORT_TYPE_SELECTED = 'gtm_report_type_selected',
  REPORT_CREATE_STARTED = 'gtm_report_create_started',
  REPORT_CREATE_COMPLETED = 'gtm_report_create_completed',
  REPORT_GUEST_SAVE_CLICK = 'gtm_report_guest_save_click',
  REPORT_GUEST_SAVED = 'gtm_report_guest_saved',
  REPORT_VIEWED = 'gtm_report_viewed',
  REPORT_SEARCH_USED = 'gtm_report_search_used',
  REPORT_SORT_CHANGED = 'gtm_report_sort_changed',
  REPORT_PAGE_OPENED_INDEXED = 'gtm_report_page_opened_indexed',
  REPORT_PAGE_OPENED_INDEXED_DELTA = 'gtm_report_page_opened_indexed_delta',
  REPORT_PAGE_OPENED_SKIPPED = 'gtm_report_page_opened_skipped',
  REPORT_PAGE_OPENED_SKIPPED_DELTA = 'gtm_report_page_opened_skipped_delta',
  REPORT_PAGE_OPENED_HEALTHY = 'gtm_report_page_opened_healthy',
  REPORT_PAGE_OPENED_HEALTHY_DELTA = 'gtm_report_page_opened_healthy_delta',
  REPORT_PAGE_OPENED_WARNING = 'gtm_report_page_opened_warning',
  REPORT_PAGE_OPENED_WARNING_DELTA = 'gtm_report_page_opened_warning_delta',
  REPORT_PAGE_OPENED_BROKEN = 'gtm_report_page_opened_broken',
  REPORT_PAGE_OPENED_BROKEN_DELTA = 'gtm_report_page_opened_broken_delta',
  REPORT_EXPORT_SITEMAP = 'gtm_report_export_sitemap',
  REPORT_EXPORT_PDF = 'gtm_report_export_pdf',
  REPORT_EXPORT_CSV = 'gtm_report_export_csv',
  REPORT_SHARE_LINK_COPY = 'gtm_report_share_link_copy',
  REPORT_SHARE_INVITE = 'gtm_report_share_invite',
  REPORT_RECRAWL_INIT = 'gtm_report_recrawl_init',
  REPORT_RECRAWL_COMPLETED = 'gtm_report_recrawl_completed',
  REPORT_SCHEDULE_UPGRADE = 'gtm_report_schedule_upgrade',
  REPORT_SCHEDULE_SET_DAY = 'gtm_report_schedule_set_day',
  REPORT_SCHEDULE_SET_WEEK = 'gtm_report_schedule_set_week',
  REPORT_SCHEDULE_SET_MONTH = 'gtm_report_schedule_set_month',
  REPORT_SCHEDULE_SET_DISABLE = 'gtm_report_schedule_set_disable',

  // Import from file
  IMPORT_FILE_ERROR = 'gtm_project_import_error',
  IMPORT_FILE_ERROR_2 = 'gtm_project_import_error_2',
  IMPORT_FILE_SUCCESS = 'gtm_project_import_success',
  // Estimator
  ESTIM_HIDE = 'gtm_estim_hide',
  ESTIM_HOUR_MANUAL = 'gtm_estim_hour_manual',
  ESTIM_HOUR_PAGE = 'gtm_estim_hour_page',

  // Project saving
  PR_SAVE = 'gtm_project_save',
  CRAWL_PR_SAVE = 'gtm_crawl_project_save',
  SITEMAP_XML_PR_SAVE = 'gtm_sitemapxml_project_save',
  AI_PR_SAVE = 'gtm_ai_project_save',
  TEMPLATE_PR_SAVE = 'gtm_template_project_save',
  IMPORT_PR_SAVE = 'gtm_import_project_save',

  // Seo tags
  SEO_COPY = 'gtm_seo_code_copy',
  SEO_IMPORT = 'gtm_seo_import',
  SEO_OG_IMAGE_UPLOAD = 'gtm_seo_ogimage_upload',
  SEO_TITLE = 'gtm_seo_title',
  SEO_DESCRIPTION = 'gtm_seo_description',
  SEO_SLUG = 'gtm_seo_slug',
  SEO_H1 = 'gtm_seo_h1',
  SEO_KEYWORD = 'gtm_seo_keyword',
  SEO_INTENT = 'gtm_seo_intent',

  // Google Analytics in SEO mode. Not `ga_…`: GA4 reserves that prefix.
  ANALYTICS_CONNECT = 'gtm_analytics_connect',
  ANALYTICS_METRICS_VIEW = 'gtm_analytics_metrics_view',

  // Export
  EXPORT_CSV = 'gtm_export_csv',
  EXPORT_XML = 'gtm_export_xml',
  EXPORT_FIGMA = 'gtm_export_figma',
  EXPORT_PNG = 'gtm_export_png',
  EXPORT_SITEMAP_XML = 'gtm_export_sitemap-xml',
  EXPORT_PDF = 'gtm_export_pdf',
  EXPORT_TXT = 'gtm_export_txt',
  EXPORT_REQUEST = 'gtm_export_request',
  EXPORT_WP = 'gtm_export_wp',
  EXPORT_EXCEL = 'gtm_export_excel',
  EXPORT_PROMPT = 'gtm_export_prompt_view',
  EXPORT_PROMPT_COPY = 'gtm_export_prompt_copy',
  EXPORT_DOCX = 'gtm_export_word',

  // Auth
  AUTH_GOOGLE = 'gtm_auth_google',
  AUTH_RESET_PASS = 'gtm_auth_reset_pass',
  AUTH_SIGN_UP = 'gtm_auth_sign_up',
  AUTH_SETUP_COMPLETE = 'gtm_auth_setup_complete',
  AUTH_LOG_IN = 'gtm_auth_log_in',
  AUTH_EMAIL_VERIFIED = 'gtm_auth_email_verificate',
  AUTH_MODAL_OPEN = 'gtm_auth_modal_open',
  AUTH_SIGN_UP_ERROR = 'gtm_auth_signup_error',
  AUTH_LOG_IN_ERROR = 'gtm_auth_login_error',
  AUTH_OTP_ERROR = 'gtm_auth_otp_error',

  // Node
  NODE_COLLAPSE = 'gtm_page_collapse',

  // Block
  BLOCK_IMAGE = 'gtm_block_image',
  BLOCK_COMPLETE = 'gtm_block_complete',
  BLOCK_COLOR = 'gtm_block_color',
  BLOCK_COMMENT_ADD = 'gtm_comment_block',
  BLOCK_COLLAPSE = 'gtm_content_block_collapse',
  BLOCK_EXPAND = 'gtm_content_block_expand',
  BLOCKS_HIDE = 'gtm_blocks_hide',
  BLOCK_ADD_BY_CONTENT = 'gtm_content_block_add',
  BLOCK_MOVE = 'gtm_content_block_move',
  BLOCK_DUPLICATE = 'gtm_content_block_duplicate',
  BLOCK_REMOVE = 'gtm_content_block_remove',
  BLOCK_ADD = 'gtm_block_add',

  // Content
  CONTENT_EXPORT_PDF = 'gtm_content_export_pdf',

  // Symbol
  SYMBOL_ADD_TOOLBAR = 'gtm_symbol_add_toolbar',
  SYMBOL_ADD_SIDEBAR = 'gtm_symbol_add_sidebar',

  // Image
  IMAGE_VIEW = 'gtm_image_view',
  IMAGE_REMOVE = 'gtm_image_remove',
  IMAGE_UPLOAD = 'gtm_image_upload',
  IMAGE_FIGMA = 'gtm_image_figma',

  // Wireframes
  WF_ADD = 'gtm_wireframe_add',
  WF_MULTIPLE = 'gtm_wireframe_multiple',
  WF_REQUEST = 'gtm_wireframe_request',

  // Project
  PROJECT_RENAME = 'gtm_project_rename',
  PROJECT_UPLOAD_CUSTOM_LOGO = 'gtm_project_logo_upload',
  PROJECT_THEME = 'gtm_project_theme',
  PROJECT_FRAME = 'gtm_project_frame',
  PROJECT_LAYOUT = 'gtm_project_layout',
  PROJECT_ARCHIVE = 'gtm_project_archive',
  PROJECT_UNARCHIVE = 'gtm_project_unarchive',
  PROJECT_DELETE = 'gtm_project_delete',
  PROJECT_DUPLICATE = 'gtm_project_duplicate',
  PROJECT_RESTORE = 'gtm_project_restore',
  PROJECT_MOVE = 'gtm_project_move',
  PROJECT_TRANSFER = 'gtm_project_transfer',
  PROJECT_TAB_ADD = 'gtm_project_tab_add',
  PROJECT_TAB_DUPLICATE = 'gtm_project_tab_duplicate',
  PROJECT_INVITE = 'gtm_project_invite',
  PROJECT_COPYWRITER = 'gtm_project_copywriter',

  // Tags
  TAGS_ADD_TO_PAGE = 'gtm_tag_add',
  TAGS_ADD_NEW = 'gtm_tags_new',
  TAGS_FILTER = 'gtm_tags_filter',

  // Page
  PAGE_FRAME = 'gtm_page_frame',
  PAGE_LINK = 'gtm_page_link',
  PAGE_COMMENT_ADD = 'gtm_comment_page',
  PAGE_ADD = 'gtm_page_add',

  // Sticky note
  STICKY_NOTE_ADD = 'gtm_sticky_add',

  // Team
  TEAM_INVITE = 'gtm_team_invite',
  TEAM_MEMBER_REMOVE = 'gtm_team_member_remove',
  TEAM_MEMBER_SUSPEND = 'gtm_team_member_suspend',
  TEAM_MEMBER_UNSUSPEND = 'gtm_team_member_unsuspend',
  TEAM_MEMBER_MAKE_MANAGER = 'gtm_team_member_make_manager',
  TEAM_MEMBER_MAKE_MEMBER = 'gtm_team_member_make_member',
  TEAM_LEAVE = 'gtm_team_leave',

  // Sections
  SECTION_ADD = 'gtm_sections_add',

  // External link
  EXTERNAL_LINK_ADD = 'gtm_extlink_add',

  // Templates
  TEMPLATE_USE = 'gtm_template_use',
  TEMPLATE_SAVE = 'gtm_template_save',
  TEMPLATE_UPGRADE_CLICK = 'gtm_template_upgrade_click',

  // Welcome promo
  WELCOME_AI_GENERATE = 'gtm_welcome_ai_generate',
  WELCOME_CRAWLER_GENERATE = 'gtm_welcome_crawl_go',

  // Embed
  EMBED_COPY = 'gtm_project_embed',

  // Colors
  COLOR_ADD = 'gtm_color_add',
  COLOR_EDIT = 'gtm_color_edit',
  COLOR_LEGEND_HIDE = 'gtm_color_legend_hide',

  // Upgrade
  UPGRADE_EXPORT = 'gtm_upgrade_export',
  UPGRADE_COLORS = 'gtm_upgrade_colors',
  UPGRADE_TABS = 'gtm_upgrade_tabs',
  UPGRADE_FRAMES = 'gtm_upgrade_frames',
  UPGRADE_HISTORY = 'gtm_upgrade_history',
  UPGRADE_WIREFRAMES = 'gtm_upgrade_wireframes',
  UPGRADE_TEAM = 'gtm_upgrade_team',
  UPGRADE_ARCHIVE = 'gtm_upgrade_archive',
  UPGRADE_PROJECT = 'gtm_upgrade_project',
  UPGRADE_SEO = 'gtm_upgrade_seo',
  UPGRADE_SYMBOLS = 'gtm_upgrade_symbols',
  UPGRADE_PASSWORD = 'gtm_upgrade_password',
  UPGRADE_PAGE_FRAME = 'gtm_upgrade_page_frame',
  UPGRADE_BLOCK_IMAGE = 'gtm_upgrade_block_image',
  UPGRADE_TAGS = 'gtm_upgrade_tags',
  UPGRADE_CRAWLER = 'gtm_upgrade_crawler',
  UPGRADE_AI_BLOCKS = 'gtm_upgrade_ai_blocks',
  UPGRADE_MEMBERS_ONLY = 'gtm_upgrade_members_only',
  UPGRADE_GUEST_ONLY = 'gtm_upgrade_guest_edit',
  UPGRADE_ALL_FEATURES = 'gtm_upgrade_all_features',
  UPGRADE_CAROUSEL = 'gtm_upgrade_carousel',
  UPGRADE_LIST = 'gtm_upgrade_list',

  // Editor
  EDITOR_BULLET = 'gtm_editor_bullet',
  EDITOR_NUMBERED = 'gtm_editor_numbered',
  EDITOR_CODE = 'gtm_editor_code',
  EDITOR_QUOTE = 'gtm_editor_quote',
  EDITOR_LEAD = 'gtm_editor_lead',
  EDITOR_TODO = 'gtm_editor_todo',
  EDITOR_COLOR_HIGHLIGHT = 'gtm_editor_color_highlight',
  EDITOR_COLOR_TEXT = 'gtm_editor_color_text',
  EDITOR_UPLOAD = 'gtm_editor_upload',
  EDITOR_CONTENT = 'gtm_editor_content',
  EDITOR_BLOCK = 'gtm_editor_block',
  EDITOR_AI_GRAMMAR = 'gtm_editor_ai_grammar',
  EDITOR_AI_SHORT = 'gtm_editor_ai_short',
  EDITOR_AI_LONG = 'gtm_editor_ai_long',
  EDITOR_AI_FRIEND = 'gtm_editor_ai_friend',
  EDITOR_AI_PROF = 'gtm_editor_ai_prof',
  EDITOR_AI_TRANSLATE = 'gtm_editor_ai_translate',
  EDITOR_AI_GENERATE = 'gtm_editor_ai_generate',
  EDITOR_INTERNAL_LINK = 'gtm_editor_internal_link',
  EDITOR_MENTION = 'gtm_editor_mention',
  EDITOR_TABLE = 'gtm_editor_table',
  EDITOR_COLUMNS = 'gtm_editor_columns',

  // Publish
  PUBLISH_GENERATE = 'gtm_publish_generate',
  PUBLISH_UPDATE = 'gtm_publish_update',
  PUBLISH_OPEN = 'gtm_publish_open',
  PUBLISH_STOP = 'gtm_publish_stop',

  // File attachments
  UPLOAD_FILE_ATTACHMENT_SUCCESS = 'gtm_attach_file',
  UPLOAD_IMAGE_ATTACHMENT_SUCCESS = 'gtm_attach_image',
  UPLOAD_FILE_ATTACHMENT_ERROR = 'gtm_attach_error',
  UPLOAD_FILE_ATTACHMENT_SIZE_ERROR = 'gtm_attach_error_size',

  // Search
  SEARCH_RESULT_CLICK = 'gtm_search_result_click',
  // Per-entity companions to SEARCH_RESULT_CLICK, which stays as it is so its
  // GA series keeps running. Without them the pages-vs-content split is
  // invisible: sendGtmEvent pushes only the event name, never the reason.
  SEARCH_BLOCK_CLICK = 'gtm_search_block_click',
  SEARCH_BLOCK_NOTE_CLICK = 'gtm_search_block_note_click',
  SEARCH_NOTE_CLICK = 'gtm_search_note_click',
  SEARCH_COMMENT_CLICK = 'gtm_search_comment_click',

  // Structure sidebar
  STRUCTURE_OPEN = 'gtm_structure_open',
  STRUCTURE_PAGE_CLICK = 'gtm_structure_page_click',

  // Artboard
  MULTISELECT_MODE_ON = 'gtm_mode_multiselect',

  // Zoom
  ZOOM_IN = 'gtm_zoom_in',
  ZOOM_OUT = 'gtm_zoom_out',
  ZOOM_CENTER = 'gtm_zoom_center',
  ZOOM_150 = 'gtm_zoom_150',
  ZOOM_100 = 'gtm_zoom_100',
  ZOOM_75 = 'gtm_zoom_75',
  ZOOM_50 = 'gtm_zoom_50',
  ZOOM_FIT = 'gtm_zoom_fit',

  //Threads
  CREATE_THREAD = 'gtm_comment_artboard',
  RESOLVE_THREAD = 'gtm_comment_artboard_resolve',
  REOPEN_THREAD = 'gtm_comment_artboard_reopen',
  COPY_THREAD_LINK = 'gtm_comment_artboard_link',
  DELETE_COMMENT = 'gtm_comment_artboard_delete',

  // Dashboard
  DASHBOARD_SORT_CREATED = 'gtm_projects_sort_datecreated',
  DASHBOARD_SORT_A_Z = 'gtm_projects_sort_a_z',
  DASHBOARD_NEW_FOLDER = 'gtm_dashboard_folder_new',
  DASHBOARD_FOLDER_UP = 'gtm_dashboard_folder_up',
  DASHBOARD_FOLDER_DOWN = 'gtm_dashboard_folder_down',
  DASHBOARD_FOLDER_DELETE = 'gtm_dashboard_folder_delete',
  DASHBOARD_FOLDER_VISIBILITY_SWITCH = 'gtm_dashboard_folder_visibility_switch',

  // API keys
  API_KEY_CREATE_READ = 'gtm_api_key_create_read',
  API_KEY_CREATE_WRITE = 'gtm_api_key_create_write',
  API_KEY_REVOKE = 'gtm_api_key_revoke',

  // Video tutorial
  DASHBOARD_INTRO = 'gtm_video_intro_dashboard',
  TEAMS_INTRO = 'gtm_video_intro_team',
  PUBLISH_INTRO = 'gtm_video_intro_publish',
  SHARE_INTRO = 'gtm_video_intro_share',
  EXPORT_INTRO = 'gtm_video_intro_export',
}

export const generateSubscriptionGtmId = (upgrade: boolean, planId: string) => {
  let shortPlanId = planId.substr(5)

  if (isTeamPlan(planId)) {
    shortPlanId = 'team'
  } else if (isProPlan(planId)) {
    shortPlanId = 'pro'
  }

  if (!upgrade) {
    if (planId === PLAN_FREE) {
      return 'gtm_subs_churn'
    }

    return `gtm_subs_downgrade_${shortPlanId}`
  }

  return `gtm_subs_${shortPlanId}`
}

type Options = {
  reason?: string
}

export const sendGtmEvent = (
  event: GtmTriggerName,
  reasonOrOptions?: string | Options
) => {
  const { reason } =
    typeof reasonOrOptions === 'string'
      ? { reason: reasonOrOptions }
      : { reason: reasonOrOptions?.reason }

  const trackingDebug = window.localStorage.getItem('trackingDebug')
  const trackingDebugAlertDisable = window.localStorage.getItem(
    'trackingDebugAlertDisable'
  )

  const shouldLogConsole =
    trackingDebug === 'true' || trackingDebug === 'console'
  const shouldAlert =
    trackingDebug === 'true' && trackingDebugAlertDisable !== 'true'

  if (shouldLogConsole) {
    // biome-ignore lint/suspicious/noConsole: intentional
    console.log(
      `GTM Custom Event: %c${event}%c. Reason: ${reason || 'Not specified'}`,
      'font-weight: bold',
      'font-weight: normal'
    )
  }
  if (shouldAlert) {
    alert(`GTM Custom Event: ${event} \n\nReason: ${reason || 'Not specified'}`)
  }

  // @ts-expect-error
  if (window.dataLayer) {
    // @ts-expect-error
    window.dataLayer.push({ event })
  }
}

type PlanTier = 'free' | 'pro' | 'team'

export const getPlanTier = (planId: string): PlanTier => {
  if (isProPlan(planId)) {
    return 'pro'
  }

  if (isTeamOrAgencyPlan(planId)) {
    return 'team'
  }

  return 'free'
}

export const pushPlanTier = (tier: PlanTier | null) => {
  // @ts-expect-error
  if (window.dataLayer) {
    // @ts-expect-error
    window.dataLayer.push({ event: 'plan_ready', plan_tier: tier })
  }
}

// Which "project saved under an account" GTM event fires, by how the project
// was created. Fired from saveProjectSaga when the project becomes owned.
export const ProjectOriginEvents: Record<ProjectOrigin, GtmTriggerName> = {
  [ProjectOrigin.ORDINARY]: GtmTriggerName.PR_SAVE,
  [ProjectOrigin.CRAWLED]: GtmTriggerName.CRAWL_PR_SAVE,
  [ProjectOrigin.AI]: GtmTriggerName.AI_PR_SAVE,
  [ProjectOrigin.TEMPLATE]: GtmTriggerName.TEMPLATE_PR_SAVE,
  [ProjectOrigin.SITEMAP_XML]: GtmTriggerName.SITEMAP_XML_PR_SAVE,
  [ProjectOrigin.DUPLICATE]: GtmTriggerName.PR_SAVE,
  [ProjectOrigin.IMPORT]: GtmTriggerName.IMPORT_PR_SAVE,
}
